import React, { useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';

const SkillsSection = () => {
  const [activeCategory, setActiveCategory] = useState('frontend');

  const skillCategories = {
    frontend: {
      title: 'Frontend Development',
      icon: '💻',
      color: 'from-blue-500 to-indigo-600',
      skills: [
        { name: 'React.js', level: 95, category: 'Framework' },
        { name: 'JavaScript/TypeScript', level: 90, category: 'Language' },
        { name: 'Next.js', level: 85, category: 'Framework' },
        { name: 'HTML5/CSS3', level: 90, category: 'Core' },
        { name: 'Tailwind CSS', level: 88, category: 'Styling' },
        { name: 'Redux/Zustand', level: 85, category: 'State Management' },
        { name: 'Webpack/Vite', level: 80, category: 'Build Tools' },
        { name: 'Jest/Testing Library', level: 82, category: 'Testing' }
      ]
    },
    backend: {
      title: 'Backend Development',
      icon: '🔧',
      color: 'from-green-500 to-emerald-600',
      skills: [
        { name: 'Java Spring Boot', level: 95, category: 'Framework' },
        { name: 'Node.js/Express.js', level: 90, category: 'Runtime/Framework' },
        { name: 'Python', level: 85, category: 'Language' },
        { name: 'RESTful APIs', level: 92, category: 'Architecture' },
        { name: 'Microservices', level: 88, category: 'Architecture' },
        { name: 'GraphQL', level: 75, category: 'API' },
        { name: 'JWT/OAuth', level: 85, category: 'Security' },
        { name: 'JUnit/Mockito', level: 88, category: 'Testing' }
      ]
    },
    devops: {
      title: 'DevOps & Cloud',
      icon: '☁️',
      color: 'from-purple-500 to-violet-600',
      skills: [
        { name: 'AWS (S3, EC2, Lambda)', level: 90, category: 'Cloud Platform' },
        { name: 'Google Cloud Platform', level: 85, category: 'Cloud Platform' },
        { name: 'Docker', level: 88, category: 'Containerization' },
        { name: 'Kubernetes', level: 82, category: 'Orchestration' },
        { name: 'Terraform', level: 78, category: 'Infrastructure as Code' },
        { name: 'Jenkins/GitHub Actions', level: 85, category: 'CI/CD' },
        { name: 'CloudFormation', level: 82, category: 'Infrastructure' },
        { name: 'Monitoring & Logging', level: 80, category: 'Operations' }
      ]
    },
    ai: {
      title: 'AI/ML & Data',
      icon: '🤖',
      color: 'from-orange-500 to-red-600',
      skills: [
        { name: 'Python', level: 88, category: 'Programming Language' },
        { name: 'TensorFlow', level: 80, category: 'ML Framework' },
        { name: 'Keras', level: 78, category: 'Neural Networks' },
        { name: 'OpenAI APIs', level: 82, category: 'Generative AI' },
        { name: 'Data Analysis', level: 80, category: 'Analytics' },
        { name: 'Machine Learning', level: 78, category: 'Algorithms' },
        { name: 'Natural Language Processing', level: 75, category: 'AI' },
        { name: 'MLOps', level: 72, category: 'Operations' }
      ]
    }
  };

  const categories = Object.keys(skillCategories);

  const SkillBar = ({ skill }) => (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-white font-medium">{skill.name}</span>
        <div className="flex items-center space-x-2">
          <Badge variant="outline" className="text-xs border-gray-600 text-gray-400">
            {skill.category}
          </Badge>
          <span className="text-blue-400 font-semibold text-sm">{skill.level}%</span>
        </div>
      </div>
      <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${skill.level}%` }}
        />
      </div>
    </div>
  );

  return (
    <section id="skills" className="py-20 relative">
      <div className="container mx-auto px-6 z-10 relative">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Technical <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Skills</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Expertise across the full technology stack, from frontend frameworks to cloud infrastructure and AI/ML integration
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center mb-12 gap-4">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`flex items-center px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeCategory === category
                  ? `bg-gradient-to-r ${skillCategories[category].color} text-white shadow-lg scale-105`
                  : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/50 border border-gray-700/50'
              }`}
            >
              <span className="mr-2 text-lg">{skillCategories[category].icon}</span>
              {skillCategories[category].title}
            </button>
          ))}
        </div>

        {/* Skills Grid */}
        <div className="max-w-6xl mx-auto">
          <Card className="bg-gray-800/40 border-gray-700/50 backdrop-blur-sm">
            <div className="p-8">
              <h3 className="text-2xl font-bold text-white mb-8 flex items-center">
                <span className="mr-3 text-2xl">{skillCategories[activeCategory].icon}</span>
                {skillCategories[activeCategory].title}
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                {skillCategories[activeCategory].skills.map((skill, index) => (
                  <SkillBar key={index} skill={skill} />
                ))}
              </div>
            </div>
          </Card>
        </div>

        {/* Certifications */}
        <div className="mt-16 text-center">
          <h3 className="text-2xl font-bold text-white mb-8">Certifications & Achievements</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              'AWS Solutions Architect - Associate',
              'McKinsey Forward Fellowship',
              'HackerRank Java Certified',
              'HackerRank React Certified',
              'Kectil Leadership Fellowship'
            ].map((cert, index) => (
              <Badge 
                key={index} 
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 text-sm font-medium hover:scale-105 transition-transform duration-200"
              >
                {cert}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SkillsSection;